import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useItineraryQueries } from './useItineraryQueries';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { ItineraryTypes } from './types';
import { ItineraryHeader } from './ItineraryHeader';
import { ItineraryHero } from './ItineraryHero';
import countriesRawData from '../../config/data/countries.json';
import { formatDateDisplay, getRelativeDaysWithOccurrence } from 'utils';
import { isHtml } from 'utils/string';
import FluidButton from 'ui/FluidButton';
import { ItineraryImage } from './items/ItineraryImage';
import { EditorQuill } from 'ui/EditorQuill/EditorQuill';
import { produce } from 'immer';
import { VerticalSpace } from 'ui/VerticalSpace';
import { v4 as uuidv4 } from 'uuid';
import { CircleIconButton } from 'ui/CircleIconButton';
import { useModal } from 'hooks/useModal';
import XIcon from 'ui/Icons/tabler/x.component.svg';
import { GeneralModal } from 'ui/GeneralModal';
import { TextInput } from 'ui/TextInput';
import { useItineraryUploader } from './useItineraryUploader';
import { useFilesInput } from 'hooks/useFilesInput';
import { useSyncState } from 'hooks/useSyncState';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import { theme } from '../../../tailwind.config';
import { SvgIcon } from 'ui/SvgIcon';
import Clock from 'ui/Icons/clock.component.svg';
import { ENetworkRequestStatus } from 'services/BackendApi';
import { useNotifications } from 'hooks/useNotifications';
import { flushSync } from 'react-dom';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';
import { useItinerarySaveAndPublishFlow } from './useItinerarySaveAndPublishFlow';
import { NumberInput } from 'ui/NumberInput';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { dragAndDrop } from '@formkit/drag-and-drop';
import classNames from 'classnames';
import { groupAndSortItineraryItems } from './utils';
import { isEqual } from 'lodash';
import { ItineraryVisibilityOptions } from './components/ItineraryVisibilityOptions';

const GreenDot = () => {
  return (
    <div
      style={{
        height: '12px',
        width: '12px',
        borderRadius: '50%',
        backgroundColor: theme['colors']['status-confirmed'],
      }}
    />
  );
};

const BlueDot = () => {
  return (
    <div
      style={{
        height: '12px',
        width: '12px',
        borderRadius: '50%',
        backgroundColor: theme['colors']['status-enquiry'],
      }}
    />
  );
};

interface IItineraryEditRouteParams {
  bookingUuid: string;
  version: string;
}

const itemTypeToLabel: Record<ItineraryTypes['ItineraryItem']['itemType'], string> = {
  accommodation: 'Accommodation',
  transfer: 'Transfer',
  groundService: 'Ground Service',
  bespoke: 'Experience',
  destinationMarker: 'Destination',
  hotelMarker: 'Stay',
  endMarker: 'End Marker',
};

const ItemBlock = ({
  item,
  bookingUuid,
  updateItemValue,
  addNote,
  updateNote,
  deleteNote,
  onAddImage,
  onAddAttachment,
  onDeleteUpload,
  isOpen,
  onToggle,
}: {
  item: ItineraryTypes['ItineraryItem'];
  bookingUuid: string;
  updateItemValue: <K extends keyof ItineraryTypes['ItineraryItem']>(
    key: keyof ItineraryTypes['ItineraryItem'],
    value: ItineraryTypes['ItineraryItem'][K]
  ) => void;
  addNote: () => void;
  updateNote: (noteUuid: string, value: string) => void;
  deleteNote: (noteUuid: string) => void;
  onAddImage: (uploadData: any) => void;
  onAddAttachment: (uploadData: any) => void;
  onDeleteUpload: (uploadIdx: number) => void;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const addUploadModalOps = useModal<{
    uploadType: 'image' | 'file';
  }>();
  const hasImageUploads = item.uploads.filter(u => u.fileType === 'image').length >= 1;
  const hasFileUploads = item.uploads.filter(u => u.fileType === 'file').length >= 1;

  return (
    <>
      <AccordionStackable
        isOpen={isOpen}
        onToggle={onToggle}
        wrapperClassName="mb-0"
        renderSummary={({ isOpen }) => (
          <span className="flex flex-row items-center justify-between w-full">
            <span className={classNames({ 'font-bold': isOpen })}>
              {itemTypeToLabel[item.itemType]} - {item.title}
            </span>
            <span className="flex items-center">
              {item.time && item.itemType !== 'destinationMarker' && item.itemType !== 'hotelMarker' && (
                <>
                  <SvgIcon className="w-[16px] h-[16px] mr-2" IconComponent={Clock} />
                  <span className="mr-6">{item.time}</span>
                </>
              )}
            </span>
          </span>
        )}
        summaryPrefixDom={
          <span className="drag-handle rounded-md ml-2 py-1 cursor-grab mt-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-grip-vertical"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M9 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
              <path d="M9 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
              <path d="M9 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
              <path d="M15 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
              <path d="M15 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
              <path d="M15 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
            </svg>
          </span>
        }
      >
        <div className="p-10px">
          {/* images */}
          <div className="flex flex-row gap-2 border border-gray-40 border-dashed bg-brown-prime-10 p-2">
            {hasImageUploads && (
              <div className="flex flex-wrap gap-2">
                {item.uploads.map((up, uploadIdx) => {
                  if (up.fileType !== 'image') {
                    return null;
                  }
                  return (
                    <div className="relative" key={up.sizes.original.path}>
                      <ItineraryImage
                        className="h-[90px] w-[90px]"
                        imgClassName="rounded"
                        upload={up}
                        index={uploadIdx}
                        key={uploadIdx}
                      />
                      <CircleIconButton
                        className="absolute top-2 right-2"
                        IconComponent={XIcon}
                        iconSize="sm"
                        type="secondary"
                        onClick={() => {
                          onDeleteUpload(uploadIdx);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex flex-col gap-2 justify-start min-w-[100px]">
              <FluidButton
                onClick={async () => {
                  const result = await addUploadModalOps.requestConfirmation({
                    uploadType: 'image',
                  });
                  if (result) {
                    onAddImage(result);
                    addUploadModalOps.handleClose();
                  }
                }}
                type="secondary"
              >
                Add Photo
              </FluidButton>
            </div>
          </div>

          <VerticalSpace height={'10px'} />

          {/* the items to edit */}
          {item.itemType === 'hotelMarker' && (
            <div className="hotel-marker flex flex-col gap-10px">
              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Description</span>
                <EditorQuill
                  placeholder="Write here a description..."
                  text={item.ancillary_description || item.hotel_description || ''}
                  onChange={content => {
                    updateItemValue('ancillary_description', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Overview</span>
                <EditorQuill
                  placeholder="Overview"
                  text={item.hotel_overview || ''}
                  onChange={content => {
                    updateItemValue('hotel_overview', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Highlights</span>
                <EditorQuill
                  placeholder="Highlights"
                  text={item.hotel_highlights || ''}
                  onChange={content => {
                    updateItemValue('hotel_highlights', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Amenities</span>
                <EditorQuill
                  placeholder="Amenities"
                  text={item.hotel_amenities || ''}
                  onChange={content => {
                    updateItemValue('hotel_amenities', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Things to be aware of</span>
                <EditorQuill
                  placeholder="Things to be aware of"
                  text={item.hotel_additionalInfo || ''}
                  onChange={content => {
                    updateItemValue('hotel_additionalInfo', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Policies and restrictions</span>
                <EditorQuill
                  placeholder="Policies and restrictions"
                  text={item.hotel_policiesAndRestrictions || ''}
                  onChange={content => {
                    updateItemValue('hotel_policiesAndRestrictions', content);
                  }}
                />
              </div>
            </div>
          )}
          {item.itemType === 'accommodation' && (
            <div className="accommodation flex flex-col gap-10px">
              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Description</span>
                <EditorQuill
                  placeholder="Write here a description..."
                  text={item.ancillary_description || item.be_description || ''}
                  onChange={content => {
                    updateItemValue('ancillary_description', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Room Benefits</span>
                <EditorQuill
                  placeholder="Room Benefits"
                  text={item.be_meta_moreInformation || ''}
                  onChange={content => {
                    updateItemValue('be_meta_moreInformation', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Room Size</span>
                <NumberInput
                  placeholder="Room Size"
                  id="min-age"
                  className="w-full"
                  inputClassName="o:placeholder:not-italic"
                  value={item.be_meta_size?.toString() || ''}
                  onChange={value => updateItemValue('be_meta_size', parseFloat(value))}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Amenities</span>
                <EditorQuill
                  placeholder="Amenities"
                  text={item.be_meta_amenities || ''}
                  onChange={content => {
                    updateItemValue('be_meta_amenities', content);
                  }}
                />
              </div>
            </div>
          )}
          {(item.itemType === 'bespoke' || item.itemType === 'groundService') && (
            <div className="bespoke-ground-service flex flex-col gap-10px">
              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Description</span>
                <EditorQuill
                  placeholder="Write here a description..."
                  text={item.ancillary_description || item.be_description || ''}
                  onChange={content => {
                    updateItemValue('ancillary_description', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Restrictions</span>
                <EditorQuill
                  placeholder="Restrictions"
                  text={item.ancillary_restrictions || ''}
                  onChange={content => {
                    updateItemValue('ancillary_restrictions', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Max People</span>
                <NumberInput
                  placeholder="Max People"
                  id="max-people"
                  className="w-full"
                  inputClassName="o:placeholder:not-italic"
                  value={item.ancillary_productRestrictions_maximumPeople?.toString() || ''}
                  onChange={value => updateItemValue('ancillary_productRestrictions_maximumPeople', parseFloat(value))}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Min Age</span>
                <NumberInput
                  placeholder="Min Age"
                  id="min-age"
                  className="w-full"
                  inputClassName="o:placeholder:not-italic"
                  value={item.ancillary_productRestrictions_minimumAge?.toString() || ''}
                  onChange={value => updateItemValue('ancillary_productRestrictions_minimumAge', parseFloat(value))}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Overview</span>
                <EditorQuill
                  placeholder="Overview"
                  text={item.ancillary_overview || ''}
                  onChange={content => {
                    updateItemValue('ancillary_overview', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Highlights</span>
                <EditorQuill
                  placeholder="Highlights"
                  text={item.ancillary_highlights || ''}
                  onChange={content => {
                    updateItemValue('ancillary_highlights', content);
                  }}
                />
              </div>
            </div>
          )}

          {item.itemType === 'transfer' && (
            <div className="transfer flex flex-col gap-10px">
              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Description</span>
                <EditorQuill
                  placeholder="Write here a description..."
                  text={item.ancillary_description || item.be_description || ''}
                  onChange={content => {
                    updateItemValue('ancillary_description', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">From</span>
                <EditorQuill
                  placeholder="From destination"
                  text={item.ancillary_transfer_from || ''}
                  onChange={content => {
                    updateItemValue('ancillary_transfer_from', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">To</span>
                <EditorQuill
                  placeholder="To destination"
                  text={item.ancillary_transfer_to || ''}
                  onChange={content => {
                    updateItemValue('ancillary_transfer_to', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Restrictions</span>
                <EditorQuill
                  placeholder="Restrictions"
                  text={item.ancillary_restrictions || ''}
                  onChange={content => {
                    updateItemValue('ancillary_restrictions', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Max People</span>
                <NumberInput
                  id="max-people"
                  placeholder="Max People"
                  className="w-full"
                  inputClassName="o:placeholder:not-italic"
                  value={item.ancillary_productRestrictions_maximumPeople?.toString() || ''}
                  onChange={value => updateItemValue('ancillary_productRestrictions_maximumPeople', parseFloat(value))}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Min Age</span>
                <NumberInput
                  id="min-age"
                  placeholder="Min Age"
                  className="w-full"
                  inputClassName="o:placeholder:not-italic"
                  value={item.ancillary_productRestrictions_minimumAge?.toString() || ''}
                  onChange={value => updateItemValue('ancillary_productRestrictions_minimumAge', parseFloat(value))}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Overview</span>
                <EditorQuill
                  placeholder="Overview"
                  text={item.ancillary_overview || ''}
                  onChange={content => {
                    updateItemValue('ancillary_overview', content);
                  }}
                />
              </div>

              <div>
                <span className="block font-pt-sans text-14px text-black mb-5px">Highlights</span>
                <EditorQuill
                  placeholder="Highlights"
                  text={item.ancillary_highlights || ''}
                  onChange={content => {
                    updateItemValue('ancillary_highlights', content);
                  }}
                />
              </div>
            </div>
          )}

          {/* the notes, if we have any */}
          {item.notes && item.notes.length > 0 && (
            <div className="flex flex-col gap-[10px] mt-10px">
              {(item.notes || []).map(note => {
                return (
                  <div key={note.uuid} className="flex flex-row gap-2 w-full items-center">
                    <EditorQuill
                      placeholder="Note content"
                      text={note.text}
                      onChange={content => updateNote(note.uuid, content)}
                      className="w-full"
                    />
                    <CircleIconButton
                      type="secondary"
                      className="ml-10px"
                      disabled={false}
                      IconComponent={XIcon}
                      iconSize="default"
                      onClick={() => {
                        deleteNote(note.uuid);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <VerticalSpace height={'10px'} />

          {/* add a note */}
          <FluidButton
            onClick={() => {
              addNote();
            }}
            type="secondary"
          >
            Add Note
          </FluidButton>

          <VerticalSpace height={'10px'} />

          {hasFileUploads && (
            <>
              <div className="flex gap-2 flex-wrap">
                {item.uploads.map((up, uploadIdx) => {
                  if (up.fileType !== 'file') {
                    return null;
                  }
                  return (
                    <div className="relative" key={up.path}>
                      <span className="bg-gray-10 text-12px text-gray-100 p-10px w-[90px] h-[90px] flex items-center">
                        <span className="w-full line-clamp-3">{up.displayName}</span>
                      </span>
                      <CircleIconButton
                        className="absolute top-2 right-2"
                        IconComponent={XIcon}
                        iconSize="sm"
                        type="secondary"
                        onClick={() => {
                          onDeleteUpload(uploadIdx);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              <VerticalSpace height={'10px'} />
            </>
          )}

          {/* add an attachment */}
          <FluidButton
            onClick={async () => {
              const result = await addUploadModalOps.requestConfirmation({
                uploadType: 'file',
              });
              if (result) {
                onAddAttachment(result);
              }
            }}
            type="secondary"
          >
            Attach File
          </FluidButton>
        </div>
      </AccordionStackable>

      {/* for uploading images and files */}
      <AddUploadModal
        isOpen={addUploadModalOps.isOpen}
        onClose={addUploadModalOps.handleClose}
        bookingUuid={bookingUuid}
        uploadType={addUploadModalOps.modalData?.uploadType!}
        displayNameOverwrite={undefined}
        handleConfirm={addUploadModalOps.handleConfirm}
        modalTitle={addUploadModalOps.modalData?.uploadType! === 'image' ? 'Add Image' : 'Add File'}
      />
    </>
  );
};

const AddUploadModal = ({
  bookingUuid,
  isOpen,
  onClose,
  uploadType,
  handleConfirm,
  modalTitle,
  displayNameOverwrite,
}: {
  bookingUuid: string;
  isOpen: boolean;
  onClose: () => void;
  uploadType: 'image' | 'file';
  handleConfirm: (data?: any) => void;
  modalTitle: string;
  displayNameOverwrite: string | undefined;
}) => {
  const [displayName, setDisplayName] = useState<string>('');
  const itineraryUploader = useItineraryUploader(bookingUuid);
  const filesInput = useFilesInput({
    accept: uploadType === 'image' ? 'image/*' : '',
  });
  const notifications = useNotifications();

  const handleClose = () => {
    filesInput.reset();
    setDisplayName('');
    onClose();
  };

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      modalWindowClassName="p-4 text-black font-pt-sans w-[587px]"
      isCloseButtonVisible={false}
    >
      <h2 className="m-0 font-noe-display text-21px text-black font-normal mb-25px">{modalTitle}</h2>
      {displayNameOverwrite === undefined && (
        <>
          <span className="block">Display Name</span>
          <TextInput
            id="add-image-modal-display-name"
            value={displayName}
            onChange={e => {
              setDisplayName(e.currentTarget.value);
            }}
          />
        </>
      )}
      <span className="block mt-10px">File</span>

      {filesInput.renderInput()}

      <div className="flex flex-row gap-2 mt-25px">
        <FluidButton
          disabled={filesInput.files.length === 0 || (displayName === '' && displayNameOverwrite === undefined)}
          type="primary"
          isLoading={itineraryUploader.requestStatus === ENetworkRequestStatus.PENDING}
          className="mt-10px"
          onClick={async () => {
            const file = filesInput.files[0];
            if (!file) {
              return;
            }
            const output = await itineraryUploader.uploadAndProcessFile({
              file,
              displayName: displayNameOverwrite || displayName,
              fileType: uploadType,
            });

            if (output) {
              // Success path - upload returned valid data
              try {
                handleConfirm(output);
                handleClose();
              } catch (e) {
                console.error(e);
                notifications.showErrorNotification(
                  `Failed to add ${uploadType === 'image' ? 'image' : 'file'}`
                );
                handleClose();
              }
            } else {
              // Failure path - upload returned null
              notifications.showErrorNotification(
                `Failed to upload ${uploadType === 'image' ? 'image' : 'file'}`
              );
              handleClose();
            }
          }}
        >
          {uploadType === 'image' ? 'Upload & Add Image' : 'Upload & Add File'}
        </FluidButton>
        <FluidButton type="secondary" className="mt-10px" onClick={handleClose}>
          Cancel
        </FluidButton>
      </div>
    </GeneralModal>
  );
};

type DetailedUploadOrUndefined = ItineraryTypes['ItineraryImageUploadOrThumbnail'] | undefined;

const DraggableItemSection = ({
  initialItems,
  bookingUuid,
  onUpdateFromDrag,
  handleUpdateItemValue,
  handleAddNote,
  handleUpdateNote,
  handleDeleteNote,
  handleOnAddImage,
  handleOnAddAttachment,
  handleOnDeleteUpload,
}: {
  initialItems: ItineraryTypes['ItineraryItem'][];
  bookingUuid: string;
  onUpdateFromDrag: (items: ItineraryTypes['ItineraryItem'][]) => void;
  handleUpdateItemValue: (key: keyof ItineraryTypes['ItineraryItem'], value: any, itemUuid: string) => void;
  handleAddNote: (itemUuid: string) => void;
  handleUpdateNote: (itemUuid: string, noteUuid: string, value: string) => void;
  handleDeleteNote: (itemUuid: string, noteUuid: string) => void;
  handleOnAddImage: (item: ItineraryTypes['ItineraryItem'], itemUuid: string, uploadData: any) => void;
  handleOnAddAttachment: (item: ItineraryTypes['ItineraryItem'], itemUuid: string, uploadData: any) => void;
  handleOnDeleteUpload: (itemUuid: string, uploadIdx: number) => void;
}) => {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);
  const [items, setItems] = useState<ItineraryTypes['ItineraryItem'][]>(initialItems);

  const itemsRef = React.useRef(items);

  // Keep ref in sync with state
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Sync local state when initialItems prop changes
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const handleSetItems = (newItems: ItineraryTypes['ItineraryItem'][]) => {
    if (!isEqual(newItems, itemsRef.current)) {
      setItems(newItems);
      onUpdateFromDrag(newItems);
    }
  };
  useEffect(() => {
    dragAndDrop({
      parent: parentRef.current!,
      getValues: () => itemsRef.current,
      setValues: handleSetItems,
      config: {
        draggingClass: '!bg-white !opacity-100',
        dragHandle: '.drag-handle',
        dropZoneClass: '!opacity-20',
        handleDragend: (data, state) => {
          data.targetData.node.el.classList.remove('!opacity-20');
        },
      },
    });
  }, []);

  return (
    <div ref={parentRef}>
      {items.map(item => {
        return (
          <ItemBlock
            key={item.uuid}
            item={item}
            bookingUuid={bookingUuid}
            isOpen={openAccordions.includes(item.uuid)}
            onToggle={() => {
              setOpenAccordions(prev => {
                const newAccordions = [...prev];
                if (newAccordions.includes(item.uuid)) {
                  newAccordions.splice(newAccordions.indexOf(item.uuid), 1);
                } else {
                  newAccordions.push(item.uuid);
                }
                return newAccordions;
              });
            }}
            updateItemValue={(key: keyof ItineraryTypes['ItineraryItem'], value: any) => {
              handleUpdateItemValue(key, value, item.uuid);
            }}
            addNote={() => {
              handleAddNote(item.uuid);
            }}
            updateNote={(noteUuid: string, value: string) => {
              handleUpdateNote(item.uuid, noteUuid, value);
            }}
            deleteNote={(noteUuid: string) => {
              handleDeleteNote(item.uuid, noteUuid);
            }}
            onAddImage={uploadData => {
              handleOnAddImage(item, item.uuid, uploadData);
            }}
            onAddAttachment={(uploadData: any) => {
              handleOnAddAttachment(item, item.uuid, uploadData);
            }}
            onDeleteUpload={(uploadIdx: number) => {
              handleOnDeleteUpload(item.uuid, uploadIdx);
            }}
          />
        );
      })}
    </div>
  );
};

export const ItineraryEditPage = () => {
  const match = useRouteMatch<IItineraryEditRouteParams>();
  const { bookingUuid, version } = match.params;
  const [isEditedWithoutSaving, _setIsEditedWithoutSaving] = useState(false);
  const itineraryQueries = useItineraryQueries(makeItineraryManagerApi(), bookingUuid, version);
  const notifications = useNotifications();
  const history = useHistory();
  const confirmPublishModal = useModal();

  const setIsEditedWithoutSaving = val => {
    _setIsEditedWithoutSaving(val);
  };
  const itinerarySaveAndPublishFlow = useItinerarySaveAndPublishFlow({
    bookingUuid,
    version,
    isEditedWithoutSaving,
    onSaveSuccess: () => {
      flushSync(() => {
        setIsEditedWithoutSaving(false);
      });
    },
    onSaveError: () => {
      flushSync(() => {
        setIsEditedWithoutSaving(false);
      });
    },
    onPublishSuccess: () => {
      flushSync(() => {
        setIsEditedWithoutSaving(false);
      });
    },
    onPublishError: () => {
      flushSync(() => {
        setIsEditedWithoutSaving(false);
      });
    },
  });

  const itineraryResponse: ItineraryTypes['ItineraryVersionResponse'] | undefined =
    itineraryQueries.getItineraryView.data?.data;
  const savedItineraryInstance = itineraryResponse ? itineraryResponse.itinerary : undefined;

  const [subtitle, setSubtitle] = useSyncState(savedItineraryInstance?.subtitle || '');
  const [datedItems, setDatedItems] = useSyncState<ItineraryTypes['ItineraryItem'][]>(
    savedItineraryInstance?.datedItems || []
  );
  const [undatedItems, setUndatedItems] = useSyncState<ItineraryTypes['ItineraryItem'][]>(
    savedItineraryInstance?.undatedItems || []
  );
  const [headerImage, setHeaderImage] = useSyncState<DetailedUploadOrUndefined>(
    itineraryResponse?.itinerary.headerImage as DetailedUploadOrUndefined
  );
  const [logoImage, setLogoImage] = useSyncState<DetailedUploadOrUndefined>(
    itineraryResponse?.itinerary.logoImage as DetailedUploadOrUndefined
  );
  const [isTripOverviewVisible, setIsTripOverviewVisible] = useSyncState<boolean>(
    savedItineraryInstance?.isTripOverviewVisible ?? false
  );
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);
  const [subtitleError, setSubtitleError] = useState<string | undefined>(undefined);

  const countryNamesForHeader = Array.from(
    new Set(
      datedItems
        .filter(i => i.itemType === 'destinationMarker')
        .map(i => countriesRawData[i.countryCode!]?.name)
        .filter(Boolean)
    )
  ).join(' - ');

  const allDates = datedItems.map(item => item.date).filter(d => d !== null && d !== undefined) as string[];
  const days = getRelativeDaysWithOccurrence(allDates);
  const totalDaysCount = days.length >= 1 ? days[days.length - 1].day : 0;
  const totalNightsCount = days.length >= 1 ? totalDaysCount - 1 : 0;

  // Create a mapping from date to day number for correct day display
  const dateToDayMap = new Map<string, number>();
  allDates.forEach((date, index) => {
    if (!dateToDayMap.has(date) && days[index]) {
      dateToDayMap.set(date, days[index].day);
    }
  });

  // Group items by date and sort by position
  const groupedByDate = groupAndSortItineraryItems(datedItems);

  const changeHeaderImageModalOps = useModal();
  const changeLogoImageModalOps = useModal();

  const handleAccordionToggle = (itemUuid: string) => {
    setOpenAccordions(prev => (prev.includes(itemUuid) ? prev.filter(id => id !== itemUuid) : [...prev, itemUuid]));
  };

  const isLatestVersion = itineraryResponse?.publishedBy === undefined;

  // if the version you're looking at is NOT the latest, you're not allowed to edit it, so redirect them away
  useEffect(() => {
    if (!isLatestVersion) {
      history.push(`/itinerary/${bookingUuid}/${version}`);
    }
  }, [isLatestVersion]);

  if (itineraryQueries.getItineraryView.isPending) {
    return (
      <div className="my-40px">
        <LoadingBar label="Loading itinerary data..." />
      </div>
    );
  }

  const handleUpdateItemValue = <K extends keyof ItineraryTypes['ItineraryItem']>(
    itemType: 'dated' | 'undated',
    key: keyof ItineraryTypes['ItineraryItem'],
    value: ItineraryTypes['ItineraryItem'][K],
    itemUuid: string
  ) => {
    if (itemType === 'dated') {
      setDatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item) {
            (item as any)[key] = value;
          }
        })
      );
      const originalItem = datedItems.find(i => i.uuid === itemUuid);
      const originalValue = originalItem?.[key];
      if (isHtml(value) && !isHtml(originalValue)) {
        // @note
        // if the new value is the HTML and the original _isn't_, then this
        // isn't _REALLY_ an update: its just the RTE kicking in and coverting the text
        // into html. so DONT mark it as edited
      } else {
        setIsEditedWithoutSaving(true);
      }
    } else if (itemType === 'undated') {
      setUndatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item) {
            (item as any)[key] = value;
          }
        })
      );
      const originalItem = undatedItems.find(i => i.uuid === itemUuid);
      const originalValue = originalItem?.[key];
      if (isHtml(value) && !isHtml(originalValue)) {
        // @note
        // if the new value is the HTML and the original _isn't_, then this
        // isn't _REALLY_ an update: its just the RTE kicking in and coverting the text
        // into html. so DONT mark it as edited
      } else {
        setIsEditedWithoutSaving(true);
      }
    }
  };

  const handleAddNote = (itemType: 'dated' | 'undated', itemUuid: string) => {
    if (itemType === 'dated') {
      setDatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item) {
            if (item.notes === undefined) {
              item.notes = [];
            }
            item.notes.push({
              uuid: uuidv4(),
              text: '',
            });
          }
        })
      );
    } else if (itemType === 'undated') {
      setUndatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item) {
            if (item.notes === undefined) {
              item.notes = [];
            }
            item.notes.push({
              uuid: uuidv4(),
              text: '',
            });
          }
        })
      );
    }
    setIsEditedWithoutSaving(true);
  };

  const handleUpdateNote = (itemType: 'dated' | 'undated', itemUuid: string, noteUuid: string, value: string) => {
    if (itemType === 'dated') {
      setDatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item && item.notes) {
            item.notes = item.notes.map(note => {
              if (note.uuid === noteUuid) {
                note.text = value;
              }
              return note;
            });
          }
        })
      );
    } else if (itemType === 'undated') {
      setUndatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item && item.notes) {
            item.notes = item.notes.map(note => {
              if (note.uuid === noteUuid) {
                note.text = value;
              }
              return note;
            });
          }
        })
      );
    }
    setIsEditedWithoutSaving(true);
  };

  const handleDeleteNote = (itemType: 'dated' | 'undated', itemUuid: string, noteUuid: string) => {
    if (itemType === 'dated') {
      setDatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item && item.notes) {
            item.notes = item.notes.filter(note => note.uuid !== noteUuid);
          }
        })
      );
    } else if (itemType === 'undated') {
      setUndatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item && item.notes) {
            item.notes = item.notes.filter(note => note.uuid !== noteUuid);
          }
        })
      );
    }
    setIsEditedWithoutSaving(true);
  };

  const handleOnAddImage = (
    itemType: 'dated' | 'undated',
    item: ItineraryTypes['ItineraryItem'],
    itemUuid: string,
    uploadData: any
  ) => {
    if (!uploadData) {
      // Defensive check - should never happen now, but just in case
      notifications.showErrorNotification('Upload failed - no data received');
      return;
    }

    if (itemType === 'dated') {
      setDatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const foundItem = draftItems.find(i => i.uuid === itemUuid);
          if (foundItem) {
            foundItem.uploads.push(uploadData);
          }
        })
      );
    } else if (itemType === 'undated') {
      setUndatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const foundItem = draftItems.find(i => i.uuid === itemUuid);
          if (foundItem) {
            foundItem.uploads.push(uploadData);
          }
        })
      );
    }
    setIsEditedWithoutSaving(true);
    notifications.showSuccessNotification(`Photo added to "${item.title}"`);
  };

  const handleOnAddAttachment = (
    itemType: 'dated' | 'undated',
    item: ItineraryTypes['ItineraryItem'],
    itemUuid: string,
    uploadData: any
  ) => {
    if (!uploadData) {
      // Defensive check - should never happen now, but just in case
      notifications.showErrorNotification('Upload failed - no data received');
      return;
    }

    if (itemType === 'dated') {
      setDatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const foundItem = draftItems.find(i => i.uuid === itemUuid);
          if (foundItem) {
            foundItem.uploads.push(uploadData);
          }
        })
      );
    } else if (itemType === 'undated') {
      setUndatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const foundItem = draftItems.find(i => i.uuid === itemUuid);
          if (foundItem) {
            foundItem.uploads.push(uploadData);
          }
        })
      );
    }
    setIsEditedWithoutSaving(true);
    notifications.showSuccessNotification(`Attachment added to "${item.title}"`);
  };

  const handleOnDeleteUpload = (itemType: 'dated' | 'undated', itemUuid: string, uploadIdx: number) => {
    if (itemType === 'dated') {
      setDatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item) {
            item.uploads.splice(uploadIdx, 1);
          }
        })
      );
    } else if (itemType === 'undated') {
      setUndatedItems(prevItems =>
        produce(prevItems, draftItems => {
          const item = draftItems.find(i => i.uuid === itemUuid);
          if (item) {
            item.uploads.splice(uploadIdx, 1);
          }
        })
      );
    }
    setIsEditedWithoutSaving(true);
  };

  const updateDatedItemsOrder = (date: string, newOrder: ItineraryTypes['ItineraryItem'][]) => {
    setDatedItems(prevItems => {
      // Create a map of uuid to new items for quick lookup
      const newItemsMap = new Map(newOrder.map(item => [item.uuid, item]));

      // Update the items with matching date
      return prevItems.map(item => {
        if (item.date === date && newItemsMap.has(item.uuid)) {
          return newItemsMap.get(item.uuid)!;
        }
        return item;
      });
    });
  };

  return (
    <>
      <div className="mb-40px font-pt-sans">
        {/* the header */}
        <div className="sticky w-full my-0 top-0 left-0 right-0 bg-white z-[9999]">
          <ItineraryHeader
            isLatestVersion={isLatestVersion}
            upload={logoImage}
            itineraryUuid={bookingUuid}
            version={version}
            mode={'edit'}
            onSave={() => {
              if (!subtitle.trim()) {
                setSubtitleError('Subtitle is required');
                return;
              }
              itinerarySaveAndPublishFlow.onSave({
                savedItineraryInstance,
                datedItems,
                undatedItems,
                subtitle,
                logoImage,
                headerImage,
                isTripOverviewVisible,
              });
            }}
            onPublish={() => {
              if (!subtitle.trim()) {
                setSubtitleError('Subtitle is required');
                return;
              }
              itinerarySaveAndPublishFlow.onPublish({
                savedItineraryInstance,
                datedItems,
                undatedItems,
                subtitle,
                logoImage,
                headerImage,
                isTripOverviewVisible,
              });
            }}
            isSaveLoading={itinerarySaveAndPublishFlow.isSavePending}
            isPublishLoading={itinerarySaveAndPublishFlow.isPublishPending}
            changeLogoButton={
              <FluidButton
                type="secondary"
                onClick={async () => {
                  const result = await changeLogoImageModalOps.requestConfirmation();
                  if (result) {
                    setLogoImage(result);
                    setIsEditedWithoutSaving(true);
                    notifications.showSuccessNotification('Logo photo uploaded');
                  }
                }}
              >
                Change Logo
              </FluidButton>
            }
            canViewItineraryLinks={true}
          />
        </div>

        <div className="container w-1280px mx-auto mt-[40px]">
          <div className="flex justify-between items-center mb-20px">
            <h1 className="m-0 font-noe-display font-normal text-[36px] text-black">Edit Itinerary</h1>

            {itineraryResponse && (
              <div className="flex flex-col text-[15px] text-black">
                <span className="text-right text-13px">
                  V{itineraryResponse.version}, {formatDateDisplay(itineraryResponse.updatedBy.timestamp)} /{' '}
                  {itineraryResponse.createdBy.email}
                </span>

                {itineraryResponse.publishedBy ? (
                  <span className="text-13px flex items-center gap-2 justify-end">
                    Published <GreenDot />
                  </span>
                ) : (
                  <span className="text-13px flex items-center gap-2 justify-end">
                    Not Published <BlueDot />
                  </span>
                )}
              </div>
            )}
          </div>

          <span className="font-pt-sans font-bold text-[17px] text-black mb-4 block">Main Info</span>

          {/* Visibility Options */}
          <ItineraryVisibilityOptions
            isTripOverviewVisible={isTripOverviewVisible}
            onTripOverviewVisibilityChange={isVisible => {
              setIsTripOverviewVisible(isVisible);
              setIsEditedWithoutSaving(true);
            }}
          />

          {/* the hero image */}
          <div className="relative">
            {headerImage && (
              <ItineraryHero
                upload={headerImage}
                title={countryNamesForHeader}
                subtitles={[`${totalDaysCount} Days / ${totalNightsCount} Nights`, subtitle]}
              />
            )}
            <FluidButton
              className="absolute left-[50%] top-[75%] translate-x-[-50%] translate-y-[-50%]"
              type="secondary"
              onClick={async () => {
                const result = await changeHeaderImageModalOps.requestConfirmation();
                if (result) {
                  setHeaderImage(result);
                  setIsEditedWithoutSaving(true);
                  notifications.showSuccessNotification('Header photo uploaded');
                }
              }}
            >
              Change Header
            </FluidButton>

            <TextInput
              id="hero-subtitle"
              className="mt-10px"
              placeholder="Itinerary subtitle"
              value={subtitle}
              errorMessage={subtitleError}
              onChange={e => {
                setSubtitle(e.currentTarget.value);
                setIsEditedWithoutSaving(true);
                if (subtitleError) setSubtitleError(undefined);
              }}
            />
          </div>

          {Object.entries(groupedByDate).map(([date, items]) => {
            const dayNumber = dateToDayMap.get(date) || 1;
            return (
              <div key={date} className="mb-4">
                <p>
                  <span className="font-bold text-black">Day {dayNumber}</span> ({formatDateDisplay(date)})
                </p>
                <DraggableItemSection
                  bookingUuid={bookingUuid}
                  initialItems={items}
                  onUpdateFromDrag={reorderedItems => {
                    const itemsWithPositions = reorderedItems.map((item, index) => ({
                      ...item,
                      position: index,
                    }));
                    updateDatedItemsOrder(date, itemsWithPositions);
                    setIsEditedWithoutSaving(true);
                  }}
                  handleUpdateItemValue={(key, value, itemUuid) => {
                    handleUpdateItemValue('dated', key, value, itemUuid);
                  }}
                  handleAddNote={itemUuid => {
                    handleAddNote('dated', itemUuid);
                  }}
                  handleUpdateNote={(itemUuid, noteUuid, value) => {
                    handleUpdateNote('dated', itemUuid, noteUuid, value);
                  }}
                  handleDeleteNote={(itemUuid, noteUuid) => {
                    handleDeleteNote('dated', itemUuid, noteUuid);
                  }}
                  handleOnAddImage={(item, itemUuid, uploadData) => {
                    handleOnAddImage('dated', item, itemUuid, uploadData);
                  }}
                  handleOnAddAttachment={(item, itemUuid, uploadData) => {
                    handleOnAddAttachment('dated', item, itemUuid, uploadData);
                  }}
                  handleOnDeleteUpload={(itemUuid, uploadIdx) => {
                    handleOnDeleteUpload('dated', itemUuid, uploadIdx);
                  }}
                />
              </div>
            );
          })}

          {undatedItems.length >= 1 && (
            <div>
              <p>
                <span className="font-bold">Unscheduled Items</span>
              </p>
              {undatedItems.map(item => {
                return (
                  <ItemBlock
                    key={item.uuid}
                    item={item}
                    bookingUuid={bookingUuid}
                    isOpen={openAccordions.includes(item.uuid)}
                    onToggle={() => handleAccordionToggle(item.uuid)}
                    updateItemValue={(key: keyof ItineraryTypes['ItineraryItem'], value: any) => {
                      handleUpdateItemValue('undated', key, value, item.uuid);
                    }}
                    addNote={() => {
                      handleAddNote('undated', item.uuid);
                    }}
                    updateNote={(noteUuid: string, value: string) => {
                      handleUpdateNote('undated', item.uuid, noteUuid, value);
                    }}
                    deleteNote={(noteUuid: string) => {
                      handleDeleteNote('undated', item.uuid, noteUuid);
                    }}
                    onAddImage={uploadData => {
                      handleOnAddImage('undated', item, item.uuid, uploadData);
                    }}
                    onAddAttachment={(uploadData: any) => {
                      handleOnAddAttachment('undated', item, item.uuid, uploadData);
                    }}
                    onDeleteUpload={(uploadIdx: number) => {
                      handleOnDeleteUpload('undated', item.uuid, uploadIdx);
                    }}
                  />
                );
              })}
            </div>
          )}

          <AddUploadModal
            bookingUuid={bookingUuid}
            uploadType="image"
            displayNameOverwrite={changeHeaderImageModalOps.isOpen ? 'Header Photo' : 'Logo Image'}
            isOpen={changeHeaderImageModalOps.isOpen || changeLogoImageModalOps.isOpen}
            modalTitle={changeHeaderImageModalOps.isOpen ? 'Change Header Photo' : 'Change Logo Image'}
            onClose={() => {
              // just close 'em both
              changeHeaderImageModalOps.handleClose();
              changeLogoImageModalOps.handleClose();
            }}
            handleConfirm={data => {
              if (changeHeaderImageModalOps.isOpen) {
                changeHeaderImageModalOps.handleConfirm(data);
              } else if (changeLogoImageModalOps.isOpen) {
                changeLogoImageModalOps.handleConfirm(data);
              }
            }}
          />
        </div>
      </div>
      <LeaveWithoutSavingModal when={isEditedWithoutSaving} />

      {confirmPublishModal.isOpen && (
        <ConfirmationModal
          className="leave-without-saving z-30"
          type={EConfirmationModalType.WARNING}
          isOpen
          title={`You are about to publish this version. This action will create a new published version that can be shared.`}
          message={'Are you sure?'}
          confirmButtonLabel={'Yes'}
          cancelButtonLabel={'No'}
          onConfirm={() => {
            confirmPublishModal.handleConfirm(true);
          }}
          onCancel={() => {
            confirmPublishModal.handleClose();
          }}
        />
      )}

      {itinerarySaveAndPublishFlow.render()}
    </>
  );
};
